/**
 * Habit-side realism detection (R4.2/R4.3, `docs/specs/phase-2c-weekly-
 * review.md` WR-7/WR-8) -- a NEW, SEPARATE module from `../forecast/
 * objective-forecast.ts` per ADR-0010 Decision 1. Not an extension of that
 * file: objectives forecast a CONTINUOUS pace against a calendar date;
 * habits compare a DISCRETE weekly count against a fixed weekly threshold
 * over whole CLOSED Mon-Sun weeks. Different math, different output shape
 * (`AdjustmentSuggestionView`'s `TargetAdjustment` vs. `ObjectiveForecastView`'s
 * date fields) -- see that ADR for the full reasoning. This module must
 * never be merged back into `objective-forecast.ts`.
 *
 * Algorithm (ADR-0010 leaves the exact formula to dev-core-domain's
 * judgment, "median of recent actuals, tapering toward goal" per the parked
 * `desktop-weekly-review.md` §1.4 framing -- documented here per that same
 * doc's own convention of fixing the algorithm in the implementation, not
 * the type):
 *
 * 1. Gate: the two most recently CLOSED weeks strictly before `asOfWeek`
 *    (ADR-0006: "the two most recently ended weeks before the review's
 *    week-end") must BOTH be misses (build: actual < planned) or BOTH be
 *    exceedances (avoid: actual > planned). A single miss, or two misses
 *    broken by an intervening hit, never triggers a suggestion (AC-W9) --
 *    this only ever looks at exactly the two weeks immediately preceding
 *    `asOfWeek`, not "2 misses somewhere in a longer trend".
 * 2. `consecutiveMissedWeeks` reported is always 2 in this implementation
 *    (the gate itself only ever examines 2 weeks) -- a longer than the two
 *    just-checked run is not distinguished further; the contract does not
 *    require reporting a longer run than what the same 2-week gate detects,
 *    and the ADR's "2 or more consecutive" language is satisfied by "exactly
 *    the last 2 both missed" (a 3rd, 4th, ... consecutive miss before that
 *    is still "2 or more" from the review's perspective and does not change
 *    what a fresh suggestion needs to say this week).
 * 3. Suggested number: the MEDIAN of the two flagged weeks' actuals (with 2
 *    data points, median = arithmetic mean), then:
 *    - Build: floored (never rounds UP to something not yet achieved),
 *      clamped to be strictly less than the current target AND >= 1 (WR-7:
 *      "achievable... and still meaningfully a build target"). If the
 *      floored median lands at or above the current target (e.g. actuals of
 *      2 and 3 against a target of 3 -- a near-miss, not a true shortfall),
 *      clamp down to `target - 1` so a suggestion NEVER proposes a target
 *      that isn't strictly lower than today's.
 *    - Avoid: ceiled (never rounds DOWN below what was actually happening --
 *      a taper must not overstate how much things have improved), clamped
 *      to be strictly BETWEEN the current allowance and the higher of the
 *      two flagged weeks' actual lapse counts (WR-8: "strictly between the
 *      current allowance and the actual lapse count... a step down from how
 *      much I'm actually lapsing toward the original goal, never resetting
 *      straight to the original strict limit, and never proposing an
 *      allowance that doesn't tighten anything"). If the ceiled median would
 *      land at or below the current allowance (no taper needed) or at or
 *      above the max actual (no tightening), it is clamped to the open
 *      interval (allowance, maxActual) via `allowance + 1` /`maxActual - 1`
 *      respectively; when that interval is empty (maxActual <= allowance +
 *      1, i.e. only one lapse over), no suggestion is produced at all --
 *      there is no strictly-between value to propose, and proposing the
 *      boundary itself would violate WR-8's "never proposing an allowance
 *      that doesn't tighten anything" (a suggestion equal to maxActual
 *      tightens nothing versus what already happened).
 */

import type {
  AdjustmentSuggestionView,
  CompassEvent,
  HabitId,
  TargetAdjustment,
  WeekStart,
} from '../../contracts';
import { countBuildCompletionsInWeek, countLapsesInWeek } from '../habits/logging';
import { buildHabitStates, weeklyPlanMet, weeklyPlannedFor } from '../habits/state';
import { addDaysToDate, weekOf } from '../habits/week';

const WEEK_DAYS = 7;

/** The Monday of the week `n` weeks before `weekStart`. */
function weeksBefore(weekStart: WeekStart, n: number): WeekStart {
  return weekOf(addDaysToDate(weekStart, -n * WEEK_DAYS));
}

interface WeekSignal {
  readonly weekStart: WeekStart;
  readonly planned: number;
  readonly actual: number;
  readonly met: boolean;
}

/** One habit's planned/actual for one closed week, using the schedule active THAT week (ADR-0006: past weeks are never re-judged by later targets). */
function weekSignalFor(
  events: readonly CompassEvent[],
  habitId: HabitId,
  weekStart: WeekStart,
  planned: number,
  kind: 'build' | 'avoid',
): WeekSignal {
  const actual =
    kind === 'build'
      ? countBuildCompletionsInWeek(events, habitId, weekStart)
      : countLapsesInWeek(events, habitId, weekStart);
  const met = weeklyPlanMet(kind, actual, planned);
  return { weekStart, planned, actual, met };
}

/**
 * The planned target/allowance for a habit as of a given closed week. This
 * implementation uses the CURRENT (as-of-`asOfWeek`-review-time) plan for
 * both flagged weeks, matching how `HabitWeekReport.suggestion`'s consumer
 * (the live habit) will apply the adjustment -- proposing a change relative
 * to what is planned NOW, not to a stale historical target the habit has
 * since moved on from. (ADR-0006's "schedule active that week" rule governs
 * `WeekOutcome.trend` rendering in the review derivation, WS1; this
 * suggestion engine intentionally reads today's live plan as the baseline
 * for "what would a lower target even mean," since suggesting a change to a
 * target that's already been superseded would be incoherent.)
 */
function currentPlanned(
  events: readonly CompassEvent[],
  habitId: HabitId,
): { readonly kind: 'build' | 'avoid'; readonly planned: number } | null {
  const state = buildHabitStates(events).get(habitId);
  if (!state) return null;
  return { kind: state.plan.habitKind, planned: weeklyPlannedFor(state.plan) };
}

function median2(a: number, b: number): number {
  return (a + b) / 2;
}

/**
 * Detect a habit-realism suggestion for one habit as of `asOfWeek` (the
 * review week being computed) -- the two most recently CLOSED weeks strictly
 * before `asOfWeek` (i.e. `asOfWeek - 1` and `asOfWeek - 2`). Returns `null`
 * when the habit is unknown, has fewer than 2 closed weeks of history to
 * examine, or the 2-week gate is not met (WR-7/WR-8/AC-W8/AC-W9/AC-W10).
 */
export function computeHabitRealismSuggestion(
  events: readonly CompassEvent[],
  habitId: HabitId,
  asOfWeek: WeekStart,
): AdjustmentSuggestionView | null {
  const plan = currentPlanned(events, habitId);
  if (!plan) return null;

  const weekN1 = weeksBefore(asOfWeek, 1);
  const weekN2 = weeksBefore(asOfWeek, 2);

  const signalN1 = weekSignalFor(events, habitId, weekN1, plan.planned, plan.kind);
  const signalN2 = weekSignalFor(events, habitId, weekN2, plan.planned, plan.kind);

  if (signalN1.met || signalN2.met) return null;

  return plan.kind === 'build'
    ? buildSuggestion(habitId, plan.planned, signalN1, signalN2, asOfWeek)
    : avoidSuggestion(habitId, plan.planned, signalN1, signalN2, asOfWeek);
}

function buildSuggestion(
  habitId: HabitId,
  currentTarget: number,
  weekN1: WeekSignal,
  weekN2: WeekSignal,
  asOfWeek: WeekStart,
): AdjustmentSuggestionView | null {
  const medianActual = median2(weekN1.actual, weekN2.actual);
  const proposed = Math.min(Math.floor(medianActual), currentTarget - 1);
  // "Still meaningfully a build target" (WR-7): never propose < 1. A habit
  // whose actuals were 0 in both flagged weeks has nothing achievable to
  // taper to above zero -- no suggestion rather than a false floor of 1
  // that isn't actually grounded in what happened.
  if (proposed < 1) return null;

  const adjustment: TargetAdjustment = {
    habitKind: 'build',
    previous: { kind: 'times_per_week', targetPerWeek: currentTarget },
    next: { kind: 'times_per_week', targetPerWeek: proposed },
  };

  return {
    habitId,
    adjustment,
    consecutiveMissedWeeks: 2,
    reviewWeek: asOfWeek,
  };
}

function avoidSuggestion(
  habitId: HabitId,
  currentAllowance: number,
  weekN1: WeekSignal,
  weekN2: WeekSignal,
  asOfWeek: WeekStart,
): AdjustmentSuggestionView | null {
  const maxActual = Math.max(weekN1.actual, weekN2.actual);
  const medianActual = median2(weekN1.actual, weekN2.actual);

  // Strictly between currentAllowance and maxActual (WR-8) -- ceil so the
  // taper never understates how much was actually happening, then clamp
  // into the open interval. If the interval is empty (maxActual <=
  // currentAllowance + 1), there is no value that both tightens something
  // and stays achievable -- no suggestion.
  const lowerBound = currentAllowance + 1;
  const upperBound = maxActual - 1;
  if (upperBound < lowerBound) return null;

  const proposed = Math.min(Math.max(Math.ceil(medianActual), lowerBound), upperBound);

  const adjustment: TargetAdjustment = {
    habitKind: 'avoid',
    previous: { kind: 'weekly_limit', limitPerWeek: currentAllowance },
    next: { kind: 'weekly_limit', limitPerWeek: proposed },
  };

  return {
    habitId,
    adjustment,
    consecutiveMissedWeeks: 2,
    reviewWeek: asOfWeek,
  };
}
